import { useFocusEffect, useRouter } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import {
    ActivityIndicator,
    FlatList,
    Platform,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import {
    fetchVisitRequests,
    fetchVisits,
    type VisitListItem,
    type VisitRequestListItem
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getScheduleStatus } from "@/lib/schedule-status";
import { colors, fonts, type ScheduleStatusKey, scheduleStatusMeta } from "@/lib/theme";
import { useCoalescedRefetch } from "@/lib/use-coalesced-refetch";
import { useDedupedState } from "@/lib/use-deduped-state";
import { useWebSocketEvent } from "@/lib/websocket";

// ═══════════════════════════════════════════════════
// Types & date helpers
// ═══════════════════════════════════════════════════

type Tab = "upcoming" | "past";

interface ConvertedVisit {
    id: string;
    patientFirstName: string;
    patientLastName: string;
    visitType: string;
    visitTypeDisplayName: string;
    durationMinutes: number;
    time: string; // "10:00 AM"
    timeHour: number; // 10 — for intra-day sorting
    dateKey: string; // "2026-06-23"
    statusKey: ScheduleStatusKey;
    missedVisitReason: string | null;
    openForResubmission: boolean;
}

// A pending visit *request* the nurse offered — rendered as "Pending
// confirmation" on its proposed day until the agency accepts/rejects it.
interface ConvertedRequest {
    requestId: number;
    visitId: number;
    patientName: string;
    visitType: string;
    time: string;
    timeHour: number;
    dateKey: string;
}

type DayEntry =
    | { kind: "visit"; timeHour: number; visit: ConvertedVisit }
    | { kind: "request"; timeHour: number; request: ConvertedRequest };

interface DayGroup {
    dateKey: string;
    date: Date;
    primary: string; // "TODAY" / "TOMORROW" / "THURSDAY"
    secondary: string; // "Tuesday, Jun 23" / "Jun 25"
    entries: DayEntry[];
}

type ListItem =
    | { type: "header"; key: string; primary: string; secondary: string }
    | { type: "visit"; key: string; visit: ConvertedVisit; first: boolean }
    | { type: "request"; key: string; request: ConvertedRequest; first: boolean };

function getDateKey(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dateFromKey(dk: string): Date {
    const [y, m, d] = dk.split("-").map(Number);
    return new Date(y, m - 1, d);
}

function getWeekStart(d: Date): Date {
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Monday start
    const monday = new Date(d);
    monday.setDate(d.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
}

// `startTime` is a `timestamp` (no tz) the API serializes with a phantom `Z`;
// the digits are the visit's local wall-clock. Parse the ISO string directly so
// we don't shift by the device timezone. `createdAt` is a real timestamptz, so
// fall back to device-local interpretation for it.
function parseWallClock(iso: string, isWallClock: boolean) {
    if (isWallClock) {
        const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
        if (m) {
            return {
                year: Number(m[1]),
                month: Number(m[2]),
                day: Number(m[3]),
                hour: Number(m[4]),
                minute: Number(m[5])
            };
        }
    }
    const d = new Date(iso);
    return {
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        day: d.getDate(),
        hour: d.getHours(),
        minute: d.getMinutes()
    };
}

function formatTime(hour: number, minute: number): string {
    const ampm = hour >= 12 ? "PM" : "AM";
    const h12 = hour % 12 || 12;
    return `${h12}:${String(minute).padStart(2, "0")} ${ampm}`;
}

function convertVisit(item: VisitListItem): ConvertedVisit {
    const iso = item.startTime ?? item.createdAt;
    const p = parseWallClock(iso, item.startTime != null);
    return {
        id: item.id,
        patientFirstName: item.patientFirstName ?? "",
        patientLastName: item.patientLastName ?? "",
        visitType: item.visitType ?? "Visit",
        visitTypeDisplayName: item.visitTypeDisplayName ?? item.visitType ?? "Visit",
        durationMinutes: item.durationMinutes ?? 60,
        time: formatTime(p.hour, p.minute),
        timeHour: p.hour,
        dateKey: `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`,
        statusKey: getScheduleStatus(item.status, item.missedVisit),
        missedVisitReason: item.missedVisitReason,
        openForResubmission: item.openForResubmission
    };
}

function convertRequest(item: VisitRequestListItem): ConvertedRequest {
    const [h, m] = item.proposedTime.split(":").map(Number);
    return {
        requestId: item.requestId,
        visitId: item.visitId,
        patientName: item.patientName,
        visitType: item.visitType ?? "Visit",
        time: formatTime(h, m),
        timeHour: h,
        dateKey: item.proposedDate
    };
}

function dayHeaderParts(date: Date, todayKey: string): { primary: string; secondary: string } {
    const today = dateFromKey(todayKey);
    const diff = Math.round((date.getTime() - today.getTime()) / 86_400_000);
    const withWeekday = date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "short",
        day: "numeric"
    });
    if (diff === 0) {
        return { primary: "TODAY", secondary: withWeekday };
    }
    if (diff === 1) {
        return { primary: "TOMORROW", secondary: withWeekday };
    }
    if (diff === -1) {
        return { primary: "YESTERDAY", secondary: withWeekday };
    }
    return {
        primary: date.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase(),
        secondary: date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    };
}

function humanizeReason(reason: string): string {
    const s = reason.replace(/[_-]+/g, " ").trim();
    return s.charAt(0).toUpperCase() + s.slice(1);
}

// Build the day-grouped, sorted list for a tab. Upcoming = today + future
// (ascending — so the screen opens on Today); Past = strictly before today
// (descending — most recent first). Pending requests only ever sit in Upcoming.
function buildGroups(
    visits: ConvertedVisit[],
    requests: ConvertedRequest[],
    tab: Tab,
    todayKey: string
): DayGroup[] {
    const map = new Map<string, DayEntry[]>();
    const push = (dk: string, entry: DayEntry) => {
        const arr = map.get(dk) ?? [];
        arr.push(entry);
        map.set(dk, arr);
    };

    for (const v of visits) {
        const isUpcoming = v.dateKey >= todayKey;
        if (isUpcoming === (tab === "upcoming")) {
            push(v.dateKey, { kind: "visit", timeHour: v.timeHour, visit: v });
        }
    }
    if (tab === "upcoming") {
        for (const r of requests) {
            if (r.dateKey >= todayKey) {
                push(r.dateKey, { kind: "request", timeHour: r.timeHour, request: r });
            }
        }
    }

    const groups: DayGroup[] = [];
    for (const [dk, entries] of map) {
        const date = dateFromKey(dk);
        const { primary, secondary } = dayHeaderParts(date, todayKey);
        groups.push({
            dateKey: dk,
            date,
            primary,
            secondary,
            entries: entries.sort((a, b) => a.timeHour - b.timeHour)
        });
    }
    groups.sort((a, b) =>
        tab === "upcoming"
            ? a.date.getTime() - b.date.getTime()
            : b.date.getTime() - a.date.getTime()
    );
    return groups;
}

function flattenGroups(groups: DayGroup[]): ListItem[] {
    const items: ListItem[] = [];
    for (const g of groups) {
        items.push({
            type: "header",
            key: `h-${g.dateKey}`,
            primary: g.primary,
            secondary: g.secondary
        });
        g.entries.forEach((entry, idx) => {
            const first = idx === 0;
            if (entry.kind === "visit") {
                items.push({
                    type: "visit",
                    key: `v-${entry.visit.id}`,
                    visit: entry.visit,
                    first
                });
            } else {
                items.push({
                    type: "request",
                    key: `r-${entry.request.requestId}`,
                    request: entry.request,
                    first
                });
            }
        });
    }
    return items;
}

// ═══════════════════════════════════════════════════
// Row components
// ═══════════════════════════════════════════════════

function DayHeader({ primary, secondary }: { primary: string; secondary: string }) {
    const isToday = primary === "TODAY";
    return (
        <View style={styles.dayHeader}>
            <Text style={[styles.dayPrimary, isToday && styles.dayPrimaryToday]}>{primary}</Text>
            <Text style={styles.daySecondary}>{secondary}</Text>
        </View>
    );
}

function Rail({ statusKey }: { statusKey: ScheduleStatusKey }) {
    const m = scheduleStatusMeta[statusKey];
    if (m.dashed) {
        return <View style={[styles.railDashed, { borderLeftColor: m.rail }]} />;
    }
    return <View style={[styles.rail, { backgroundColor: m.rail }]} />;
}

function VisitRow({
    visit,
    first,
    onPress
}: {
    visit: ConvertedVisit;
    first: boolean;
    onPress: () => void;
}) {
    const m = scheduleStatusMeta[visit.statusKey];
    const isMissed = visit.statusKey === "missed";
    const muted =
        !visit.openForResubmission &&
        (visit.statusKey === "completed" || visit.statusKey === "cancelled");
    const timeColor = visit.statusKey === "in_progress" ? colors.textSecondary : colors.gray400;

    const meta =
        isMissed && visit.missedVisitReason
            ? humanizeReason(visit.missedVisitReason)
            : `${visit.visitTypeDisplayName} · ${visit.durationMinutes} min`;

    return (
        <TouchableOpacity
            activeOpacity={0.6}
            onPress={onPress}
            style={[styles.row, !first && styles.rowDivider]}
        >
            <Rail statusKey={visit.statusKey} />
            <Text style={[styles.rowTime, { color: timeColor }]}>{visit.time}</Text>
            <View style={styles.rowInfo}>
                <Text
                    style={[
                        styles.rowName,
                        muted && styles.rowNameMuted,
                        m.strike && styles.rowNameStrike
                    ]}
                    numberOfLines={1}
                >
                    {visit.patientFirstName} {visit.patientLastName}
                </Text>
                <Text
                    style={[
                        styles.rowMeta,
                        isMissed && { color: m.text, fontFamily: fonts.semiBold }
                    ]}
                    numberOfLines={1}
                >
                    {meta}
                </Text>
            </View>
            {visit.openForResubmission ? (
                <Text style={styles.reopenedPill}>Reopened</Text>
            ) : visit.statusKey === "scheduled" ? (
                <Text style={styles.chevron}>{"›"}</Text>
            ) : (
                <Text style={[styles.rowStatus, { color: m.text }]}>{m.label}</Text>
            )}
        </TouchableOpacity>
    );
}

function RequestRow({
    request,
    first,
    onPress
}: {
    request: ConvertedRequest;
    first: boolean;
    onPress: () => void;
}) {
    const m = scheduleStatusMeta.pending;
    return (
        <TouchableOpacity
            activeOpacity={0.6}
            onPress={onPress}
            style={[
                styles.row,
                !first && styles.rowDivider,
                m.tint ? { backgroundColor: m.tint } : null
            ]}
        >
            <Rail statusKey="pending" />
            <Text style={[styles.rowTime, { color: m.text }]}>{request.time}</Text>
            <View style={styles.rowInfo}>
                <Text style={styles.rowName} numberOfLines={1}>
                    {request.patientName}
                </Text>
                <Text style={styles.rowMeta} numberOfLines={1}>
                    {request.visitType} · you offered
                </Text>
            </View>
            <Text style={[styles.rowStatusSmall, { color: m.text }]}>{m.label}</Text>
        </TouchableOpacity>
    );
}

function EmptyState({ tab }: { tab: Tab }) {
    return (
        <View style={styles.empty}>
            <Text style={styles.emptyTitle}>
                {tab === "upcoming" ? "Nothing scheduled" : "No past visits yet"}
            </Text>
            <Text style={styles.emptyBody}>
                {tab === "upcoming"
                    ? "New visits will show up here. Check Explore for open visits to pick up."
                    : "Completed and missed visits will appear here once you've worked them."}
            </Text>
        </View>
    );
}

// ═══════════════════════════════════════════════════
// Main screen
// ═══════════════════════════════════════════════════

export default function ScheduleScreen() {
    const router = useRouter();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState<Tab>("upcoming");

    // Deduped: a background refetch that returns the same list must not
    // re-render the whole schedule.
    const [rawVisits, setRawVisits] = useDedupedState<ConvertedVisit[]>([]);
    const [rawRequests, setRawRequests] = useDedupedState<ConvertedRequest[]>([]);

    const refetchVisits = useCallback(() => {
        return fetchVisits()
            .then((data) => {
                const converted = data
                    .map((v) => {
                        try {
                            return convertVisit(v);
                        } catch (err) {
                            console.warn(`Failed to convert visit ${v.id}:`, err);
                            return null;
                        }
                    })
                    .filter((v): v is ConvertedVisit => v !== null);
                setRawVisits(converted);
            })
            .catch((err) => {
                console.warn("Failed to fetch visits:", err);
            });
    }, [setRawVisits]);

    const refetchRequests = useCallback(() => {
        return fetchVisitRequests()
            .then((data) => {
                setRawRequests(data.filter((r) => r.status === "pending").map(convertRequest));
            })
            .catch((err) => {
                console.warn("Failed to fetch visit requests:", err);
            });
    }, [setRawRequests]);

    // Refetch in place (no spinner), coalesced, not debounced: `unbroadcasted`
    // arrives on the global `rn:all` channel every ~1–3 s during a broadcast
    // burst, so a trailing 300 ms debounce fired a refetch per event (see the
    // Explore tab / 2026-07-01 slow-load incident). At most one request per
    // list in flight, at most one more queued; extra events collapse into it.
    const refreshVisits = useCoalescedRefetch(refetchVisits);
    const refreshRequests = useCoalescedRefetch(refetchRequests);

    // Visit screens remain mounted above this tab; refresh on return after
    // submission so completed clarifications no longer show as reopened.
    useFocusEffect(
        useCallback(() => {
            refreshVisits();
            refreshRequests();
            setLoading(false);
        }, [refreshVisits, refreshRequests])
    );

    useWebSocketEvent("visit:updated", (data) => {
        const { action } = data;
        if (action === "assigned" || action === "status_changed" || action === "request_accepted") {
            refreshVisits();
        }
        if (
            action === "unbroadcasted" ||
            action === "request_accepted" ||
            action === "request_rejected" ||
            action === "request_cancelled"
        ) {
            refreshRequests();
        }
    });

    const todayKey = getDateKey(new Date());

    const groups = useMemo(
        () => buildGroups(rawVisits, rawRequests, tab, todayKey),
        [rawVisits, rawRequests, tab, todayKey]
    );
    const listData = useMemo(() => flattenGroups(groups), [groups]);

    // Header summary counts.
    const summary = useMemo(() => {
        const today = dateFromKey(todayKey);
        const weekStartKey = getDateKey(getWeekStart(today));
        const weekEnd = getWeekStart(today);
        weekEnd.setDate(weekEnd.getDate() + 6);
        const weekEndKey = getDateKey(weekEnd);
        const monthPrefix = todayKey.slice(0, 7);

        const inWeek = (dk: string) => dk >= weekStartKey && dk <= weekEndKey;

        const todayCount = rawVisits.filter((v) => v.dateKey === todayKey).length;
        const weekCount = rawVisits.filter((v) => inWeek(v.dateKey)).length;
        const doneWeek = rawVisits.filter(
            (v) => v.statusKey === "completed" && inWeek(v.dateKey)
        ).length;
        const doneMonth = rawVisits.filter(
            (v) => v.statusKey === "completed" && v.dateKey.startsWith(monthPrefix)
        ).length;
        return { todayCount, weekCount, doneWeek, doneMonth };
    }, [rawVisits, todayKey]);

    const initials =
        `${(user?.firstName ?? "")[0] ?? ""}${(user?.lastName ?? "")[0] ?? ""}`.toUpperCase();

    const renderItem = useCallback(
        (item: ListItem) => {
            switch (item.type) {
                case "header":
                    return <DayHeader primary={item.primary} secondary={item.secondary} />;
                case "request":
                    return (
                        <RequestRow
                            request={item.request}
                            first={item.first}
                            onPress={() => router.push(`/available-visit/${item.request.visitId}`)}
                        />
                    );
                default:
                    return (
                        <VisitRow
                            visit={item.visit}
                            first={item.first}
                            onPress={() => router.push(`/visit/${item.visit.id}`)}
                        />
                    );
            }
        },
        [router]
    );

    return (
        <SafeAreaView edges={["top"]} style={styles.container}>
            {/* Header */}
            <View style={styles.header}>
                <View style={styles.headerTextCol}>
                    <Text style={styles.title}>Schedule</Text>
                    <Text style={styles.summary}>
                        {tab === "upcoming" ? (
                            <>
                                <Text style={styles.summaryStrong}>
                                    {summary.todayCount}{" "}
                                    {summary.todayCount === 1 ? "visit" : "visits"}
                                </Text>{" "}
                                today ·{" "}
                                <Text style={styles.summaryStrong}>{summary.weekCount}</Text> this
                                week
                            </>
                        ) : (
                            <>
                                <Text style={styles.summaryStrong}>{summary.doneWeek}</Text> done
                                this week ·{" "}
                                <Text style={styles.summaryStrong}>{summary.doneMonth}</Text> this
                                month
                            </>
                        )}
                    </Text>
                </View>
                {initials.length > 0 && (
                    <View style={styles.avatar}>
                        <Text style={styles.avatarText}>{initials}</Text>
                    </View>
                )}
            </View>

            {/* Upcoming / Past toggle */}
            <View style={styles.toggle}>
                {(["upcoming", "past"] as Tab[]).map((t) => {
                    const on = t === tab;
                    return (
                        <TouchableOpacity
                            key={t}
                            activeOpacity={0.7}
                            onPress={() => setTab(t)}
                            style={styles.toggleBtn}
                        >
                            <Text style={[styles.toggleText, on && styles.toggleTextOn]}>
                                {t === "upcoming" ? "Upcoming" : "Past"}
                            </Text>
                            {on && <View style={styles.toggleUnderline} />}
                        </TouchableOpacity>
                    );
                })}
            </View>

            {/* List */}
            {loading ? (
                <View style={styles.loading}>
                    <ActivityIndicator size="large" color={colors.teal400} />
                </View>
            ) : listData.length === 0 ? (
                <EmptyState tab={tab} />
            ) : Platform.OS === "web" ? (
                <ScrollView
                    contentContainerStyle={styles.listContent}
                    showsVerticalScrollIndicator={false}
                >
                    {listData.map((item) => (
                        <View key={item.key}>{renderItem(item)}</View>
                    ))}
                </ScrollView>
            ) : (
                <FlatList
                    style={styles.flex}
                    data={listData}
                    keyExtractor={(item) => item.key}
                    renderItem={({ item }) => renderItem(item)}
                    contentContainerStyle={styles.listContent}
                    showsVerticalScrollIndicator={false}
                />
            )}
        </SafeAreaView>
    );
}

// ═══════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.surface },
    flex: { flex: 1 },
    loading: { flex: 1, alignItems: "center", justifyContent: "center" },

    // Header
    header: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 24,
        paddingTop: 10,
        paddingBottom: 4
    },
    headerTextCol: { flex: 1 },
    title: {
        fontSize: 30,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary,
        letterSpacing: -0.8
    },
    summary: { fontSize: 14, fontFamily: fonts.medium, color: colors.textSecondary, marginTop: 5 },
    summaryStrong: { fontFamily: fonts.extraBold, color: colors.textPrimary },
    avatar: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: colors.pink400,
        alignItems: "center",
        justifyContent: "center"
    },
    avatarText: { fontSize: 14, fontFamily: fonts.extraBold, color: "#FFFFFF" },

    // Toggle
    toggle: {
        flexDirection: "row",
        gap: 22,
        paddingHorizontal: 24,
        paddingTop: 18,
        borderBottomWidth: 1,
        borderBottomColor: colors.gray100
    },
    toggleBtn: { paddingBottom: 12 },
    toggleText: { fontSize: 15, fontFamily: fonts.bold, color: colors.textTertiary },
    toggleTextOn: { color: colors.textPrimary },
    toggleUnderline: {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: -1,
        height: 2.5,
        borderRadius: 2,
        backgroundColor: colors.teal400
    },

    // List
    listContent: { paddingBottom: 120 },

    // Day header
    dayHeader: {
        flexDirection: "row",
        alignItems: "baseline",
        gap: 10,
        paddingHorizontal: 24,
        paddingTop: 24,
        paddingBottom: 6
    },
    dayPrimary: {
        fontSize: 14,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary,
        letterSpacing: 0.3
    },
    dayPrimaryToday: { color: colors.teal600 },
    daySecondary: { fontSize: 13, fontFamily: fonts.semiBold, color: colors.textTertiary },

    // Row
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: 15,
        paddingHorizontal: 24,
        paddingVertical: 15
    },
    rowDivider: { borderTopWidth: 1, borderTopColor: colors.gray100 },
    rail: { width: 3, alignSelf: "stretch", borderRadius: 3, marginVertical: 2 },
    railDashed: {
        width: 0,
        alignSelf: "stretch",
        borderLeftWidth: 3,
        borderStyle: "dashed",
        marginVertical: 2
    },
    rowTime: { width: 62, fontSize: 13, fontFamily: fonts.bold },
    rowInfo: { flex: 1, minWidth: 0 },
    rowName: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        letterSpacing: -0.3
    },
    rowNameMuted: { color: colors.gray700 },
    rowNameStrike: { color: colors.gray400, textDecorationLine: "line-through" },
    rowMeta: { fontSize: 12.5, fontFamily: fonts.medium, color: colors.textTertiary, marginTop: 2 },
    rowStatus: { fontSize: 12, fontFamily: fonts.extraBold },
    reopenedPill: {
        fontSize: 11,
        fontFamily: fonts.extraBold,
        color: colors.teal600,
        backgroundColor: colors.teal50,
        borderWidth: 1,
        borderColor: colors.teal200,
        borderRadius: 999,
        paddingHorizontal: 8,
        paddingVertical: 4,
        overflow: "hidden"
    },
    rowStatusSmall: {
        fontSize: 11,
        fontFamily: fonts.extraBold,
        maxWidth: 104,
        textAlign: "right"
    },
    chevron: { fontSize: 20, fontFamily: fonts.bold, color: colors.gray400 },

    // Empty
    empty: { flex: 1, alignItems: "center", justifyContent: "center", paddingHorizontal: 40 },
    emptyTitle: {
        fontSize: 18,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        marginBottom: 8
    },
    emptyBody: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textTertiary,
        textAlign: "center",
        lineHeight: 20
    }
});
